// roles-permissions.service.ts

import { Injectable, Injector } from '@angular/core';

import { USER_ROLE_KEY } from '../constants/global';
import { Permissions, Role } from '../constants/roles-permissions';

import { GlobalService } from './global.service';

@Injectable({
  providedIn: 'root',
})
export class RolesPermissionsService {
  private globalService?: GlobalService;
  constructor(private injector: Injector) {}

  getGlobalService() {
    if (!this.globalService) {
      this.globalService = this.injector.get(GlobalService);
    }
    return this.globalService;
  }

  /**
   * Checks if a user has a given permission.
   * The permission is checked against the Permissions object,
   * which maps resources to their respective permissions.
   * If no role is provided, the user's role is inferred from the global service.
   * @example
   * RolesPermissionsService.hasPermission('Quotes', 'view');
   *
   * RolesPermissionsService.hasPermission('Quotes', 'view', Role.QuoteSpecialist);
   *
   * RolesPermissionsService.hasPermission('Quotes', 'view', [Role.QuoteSpecialist, Role.OutsideSalesRep]);
   *
   * @param {string} resource the resource to check the permission for
   * @param {string} action the action to check the permission for
   * @param {Role|string[]} [role] the role or roles to check the permission against
   *
   * @returns {boolean} whether the user has the given permission
   */
  hasPermission(resource: string, action: string, role?: Role | string[]): boolean {
    let roles: any = role;
    let permissions: any = Permissions;
    if (!role && this.getGlobalService().getUserDetails()) {
      roles = this.getGlobalService().getUserDetails().roles;
    }
    //TODO: Need to remove
    // roles.push(Role.FGApprover)
    const resourcePermissions = permissions[resource];
    if (!resourcePermissions) {
      return false;
    }

    const allowedRoles = resourcePermissions[action];
    if (!allowedRoles) {
      return false;
    }

    return this.isRoleAllowed(allowedRoles, roles);
  }

  /**
   * Checks if a role or roles are included in an array of allowed roles.
   * If the role is a string, it is checked directly against the allowed roles.
   * If the role is an array of strings, each string is checked against the allowed roles.
   * If the role is not a string or an array of strings, it is considered to not be allowed.
   * @param {string[]} allowedRoles the array of allowed roles
   * @param {string|string[]} role the role or roles to check against the allowed roles
   * @returns {boolean} whether the role or roles are allowed
   */
  private isRoleAllowed(allowedRoles: string[], role: string | string[]): boolean {
    if (typeof role === 'string') {
      return allowedRoles.includes(role);
    } else if (Array.isArray(role)) {
      return role.some((r) => allowedRoles.includes(r));
    }
    return false;
  }
}
